import { forwardRef, useEffect, useMemo, useRef } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { MDBRow, MDBCol } from "mdbreact";
import { useParams } from "react-router-dom";

import "./FeuD3DistanceTravelled.scss";

import { Button, Label, TextArea } from "abs-ui-components";

import { FuelEuDCommonTableColumns } from "../../FeuConstants";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import { API_ROUTES } from "../../FeuApiRoutes/FeuApiRoutes";
import AutoSave from "../../../../../common/AutoSave/AutoSave";
import { AUTOSAVE_SUCCESS_MESSAGE } from "../../../Mrv/Constants";

const FeuD3DistanceTravelled = forwardRef(({ D3FeuDistanceTravelledData, refetchMonitoringData,vesselMstId  }, ref) => {
    const { control, getValues, reset, formState } = useFormContext();
    const { isDirty } = formState;
    const lastManualSave = useRef(null);
    const manualSaveTriggered = useRef(false);

    useEffect(() => {
        if (D3FeuDistanceTravelledData) {
            reset(prev => ({ ...prev, d3: D3FeuDistanceTravelledData }));
        }
    }, [D3FeuDistanceTravelledData, reset]);

    AutoSave(ref, [], async () => {
        if (!isDirty) return;
        await handleSave(true);
    });

    const handleSave = async (isAuto = false) => {
        const formValues = getValues("d3");
        const payload = d3FeuFields.reduce((acc, { name }) => {
            acc[name] = formValues?.[name] ?? "";
            return acc;
        }, {});
        try {
            const FeuD3DistanceTravelled = await callApi({
                method: "POST",
                url: API_ROUTES.D3(vesselMstId),
                data: payload,
            });
            if (FeuD3DistanceTravelled?.isSuccess) {
                emitToasterMsg("success", "Table D.3", isAuto ? AUTOSAVE_SUCCESS_MESSAGE : "Created successfully");
                reset(prev => ({ ...prev, d3: formValues }));
                if (!isAuto) {
                    lastManualSave.current = Date.now();
                }
                if (typeof refetchMonitoringData === "function") {
                    await refetchMonitoringData();
                }
            } else {
                emitToasterMsg("error", "Table D.3", FeuD3DistanceTravelled?.errorMessage);
            }
        } catch (error) {
            emitToasterMsg("error", "Table D.3", error?.errorMessage);
        }
    };

    const d3FeuFields = useMemo(() => {
        const base = FuelEuDCommonTableColumns.filter(
            f => f.name !== "backupMonitoringMethod" && f.name !== "methodDescription"
        );
        base.splice(1, 0, {
            label: "Description of method to treat data gaps",
            name: "methodDescription",
            maxLength: 4000,
        });
        return base;
    }, []);

    return (
        <div className="d3-section-form mt-4">
            <MDBRow className="mb-2">
                <MDBCol md="4">
                    <h6 className="titles-font">Title of method</h6>
                </MDBCol>
                <MDBCol md="4">
                    <h6 className="titles-font">
                        Method to treat data gaps regarding distance travelled
                    </h6>
                </MDBCol>
            </MDBRow>

            {d3FeuFields.map(({ label, name, maxLength, type }, index) => (
                <MDBRow className="align-items-center mb-3" key={index}>
                    <MDBCol md="4">
                        <Label name={label} bold />
                    </MDBCol>
                    <MDBCol md="4">
                        <Controller
                            name={`d3.${name}`}
                            control={control}
                            render={({ field }) => (
                                <TextArea
                                    id={`d3_${name}`}
                                    className="textarea-resize"
                                    value={field.value || ""}
                                    onChange={field.onChange}
                                    placeholder=""
                                    maxLength={maxLength}
                                    rows={1}
                                />
                            )}
                        />
                    </MDBCol>
                </MDBRow>
            ))}
            <div className="text-center">
                <Button id="feud3_save_btn" disabled={!isDirty} onClick={async () => {
                    manualSaveTriggered.current = true;
                    await handleSave(false);
                }}>
                    Save
                </Button>
            </div>
        </div>
    );
});

export default FeuD3DistanceTravelled;
